import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Text from "connectui/component/display/Text";
import ContextMenu from "../../../../../connectui/component/input/context/ContextMenu";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import TextInput from "../../../../../connectui/component/input/text/type/TextInput";

const useStyles = (theme) =>
  buildStyles({
    container: {
      padding: theme.spacing(0, 4),
      width: "100%",
      height: "100%",
    },
    inputContainer: {
      width: "100%",
      minHeight: "28px",
      backgroundColor: theme.colors.gray100,
      borderRadius: `${theme.radius.sm}px`,
      padding: theme.spacing(1, 2),
      cursor: "pointer",
    },
    templateBody: {
      flex: 1,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    item: {
      padding: theme.spacing(2.5, 3.5),
      cursor: "pointer",
      "&.selected": {backgroundColor: theme.colors.hoverGray},
      "&:hover": {backgroundColor: theme.colors.hoverGray},
    },
    selectedContainer: {
      width: "100%",
      overflow: "auto",
    },
  });

const WhatsappTemplateSelector = ({data, setData}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const inputContainer = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const {templates, openTemplates, freeText, preview, selected} = data;
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState({bottom: 0, left: 0});
  const [menuItems, setMenuItems] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const allTemplates = useMemo(() => {
    if (!templates?.length) return [];
    return templates.map((t) => ({
      id: t.integration_template_id,
      name: t.name,
      header: t.components.find((c) => c.type === "HEADER")?.text,
      body: t.components.find((c) => c.type === "BODY")?.text,
    }));
  }, [templates]);

  useEffect(() => {
    if (!search.trim()) setMenuItems(allTemplates);
    else {
      setMenuItems(
        allTemplates.filter((t) =>
          `${t.header?.toLowerCase() || ""}${t.name?.toLowerCase() || ""}${t.body?.toLowerCase() || ""}`.includes(
            search.toLowerCase(),
          ),
        ),
      );
    }
  }, [allTemplates, search]);

  useEffect(() => {
    if (freeText) {
      if (openTemplates) closeMenu();
      return;
    }

    if (openTemplates) return;
  }, [freeText]);

  useEffect(() => {
    if (!position.bottom && openTemplates) {
      openMenu();
    }
  }, [openTemplates]);

  const scrollToItem = (index) => {
    const menuItems = listRef.current?.querySelectorAll("div[id]");
    if (menuItems[index]) {
      menuItems[index].scrollIntoView({block: "nearest", behavior: "smooth"});
    }
  };

  const onKeyDown = (e) => {
    if (menuItems.length > 0 && openTemplates) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prevIndex) => {
          const newIndex = prevIndex < menuItems.length - 1 ? prevIndex + 1 : 0;
          scrollToItem(newIndex);
          return newIndex;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prevIndex) => {
          const newIndex =
            prevIndex > 0
              ? prevIndex > menuItems.length - 1
                ? menuItems.length - 1
                : prevIndex - 1
              : menuItems.length - 1;
          scrollToItem(newIndex);
          return newIndex;
        });
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (menuItems[selectedIndex]) {
          selectTemplate(menuItems[selectedIndex].id);
        }
      } else if (e.key === "Escape") {
        closeMenu();
      }
    } else if (!openTemplates && /^[a-zA-Z0-9]$/.test(e.key)) {
      openMenu();
    }
  };

  const openMenu = () => {
    const rect = inputContainer.current.getBoundingClientRect();
    setPosition({
      bottom: window.innerHeight - rect.top,
      left: rect.left,
      right: window.innerWidth - rect.right,
    });
    setData({...data, openTemplates: true});
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const closeMenu = () => {
    setData({...data, openTemplates: false});
    setPosition({bottom: 0, left: 0});
  };

  const selectTemplate = useCallback(
    (templateId) => {
      setData({
        ...data,
        openTemplates: false,
        selected: templates.find(
          (t) => t.integration_template_id === templateId,
        ),
      });
      setPosition({bottom: 0, left: 0});
    },
    [data, templates],
  );

  const getItems = useCallback(() => {
    return [
      {
        Component: ({label}) => (
          <FlexContainer sx={{padding: theme.spacing(2, 3.5)}}>
            <Text variant="textMD" weight="semibold">
              {label}
            </Text>
          </FlexContainer>
        ),
        label: "WhatsApp Templates",
      },
      ...menuItems.map((item, index) => ({
        Component: () => (
          <FlexContainer
            key={item.id}
            id={item.id}
            alignItems="center"
            gap={theme.spacing(2)}
            className={`${classes.item} ${
              selected?.integration_template_id === item.id ||
              selectedIndex === index
                ? "selected"
                : ""
            }`}
            onClick={() => selectTemplate(item.id)}
          >
            <SvgIcon
              component={ICONS.whatsapp}
              sx={{
                width: "20px",
                height: "20px",
                color: theme.colors.textSecondary,
              }}
            />
            <Text variant="textSM" weight="medium">
              {item.header}
            </Text>
            <Text
              variant="textSM"
              color="textTertiary"
              className={classes.templateBody}
            >
              {item.body}
            </Text>
          </FlexContainer>
        ),
      })),
    ];
  }, [menuItems, selected, selectedIndex, selectTemplate]);

  return (
    <FlexContainer
      flexDirection="column"
      className={classes.container}
      gap={theme.spacing(2)}
    >
      <ContextMenu
        menuItems={getItems()}
        open={openTemplates && !!position.bottom}
        anchorEl={inputContainer.current}
        onClose={closeMenu}
        modalProps={{customPosition: position}}
        listProps={{listRef: listRef}}
        gap={theme.spacing(2)}
        sx={{maxHeight: "400px", overflow: "auto"}}
      />
      <FlexContainer
        ref={inputContainer}
        className={classes.inputContainer}
        onClick={() => openMenu()}
      >
        <Text variant="textSM">{"/"}</Text>
        <TextInput
          inputProps={{
            ref: inputRef,
            value: search,
            autoFocus: true,
            onKeyDown,
            onChange: (e) => setSearch(e.target.value),
            style: {
              color: theme.colors.textPrimary,
              backgroundColor: "transparent",
              ...theme.typography.textSM.regular,
            },
          }}
        />
      </FlexContainer>
      {!!selected && (
        <FlexContainer
          flexDirection="column"
          gap={theme.spacing(2)}
          className={classes.selectedContainer}
        >
          <Text
            variant="textSM"
            sx={{
              whiteSpace: "break-spaces",
              wordBreak: "break-word",
              maxHeight: "45vh",
              overflow: "hidden auto",
              scrollbarWidth: "none",
            }}
          >
            {preview[selected.integration_template_id]}
          </Text>
        </FlexContainer>
      )}
    </FlexContainer>
  );
};

export default WhatsappTemplateSelector;
